import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowsClockwise, ChartBar } from "@phosphor-icons/react";
import { adspectApi, formatApiError } from "@/lib/api";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayStr() { return new Date().toISOString().slice(0, 10); }
function daysAgoStr(n) {
  const d = new Date(); d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const DATE_PRESETS = [
  { label: "Today",     from: () => todayStr(),     to: () => todayStr()      },
  { label: "Yesterday", from: () => daysAgoStr(1),  to: () => daysAgoStr(1)  },
  { label: "7 Days",    from: () => daysAgoStr(6),  to: () => todayStr()     },
  { label: "30 Days",   from: () => daysAgoStr(29), to: () => todayStr()     },
];

const MODE_COLORS = {
  Filter: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  Review: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  Money:  "bg-green-500/10 text-green-400 border-green-500/30",
  White:  "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

const LOG_PAGE_SIZE = 100;

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdspectLogsPage() {
  const [searchParams] = useSearchParams();

  // Streams list (for name lookup + selector)
  const [streams, setStreams] = useState([]);

  // Filters
  const [streamId, setStreamId] = useState(searchParams.get("stream_id") || "__all__");
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || todayStr());
  const [dateTo, setDateTo]     = useState(searchParams.get("date_to")   || todayStr());
  const [activePreset, setActivePreset] = useState(0);

  // Log data
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [fetched, setFetched]     = useState(false);
  const [page, setPage]           = useState(1);

  // Auto-load when URL params specify a stream
  const initialStreamId = searchParams.get("stream_id");

  // Load streams for selector
  const loadStreams = useCallback(async () => {
    try {
      const res = await adspectApi.listStreams();
      setStreams(res.data.streams || []);
    } catch (_) {}
  }, []);

  useEffect(() => { loadStreams(); }, [loadStreams]);

  // Auto-fetch on mount if URL has params
  useEffect(() => {
    if (initialStreamId) {
      fetchLogs({ stream_id: initialStreamId, date_from: dateFrom, date_to: dateTo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = async (opts = {}) => {
    setLoading(true);
    setPage(1);
    try {
      const sid  = opts.stream_id ?? streamId;
      const from = opts.date_from ?? dateFrom;
      const to   = opts.date_to   ?? dateTo;
      const params = { date_from: from, date_to: to, limit: 1000 };
      if (sid && sid !== "__all__") params["stream_id"] = sid;
      const res = await adspectApi.getClickLog(params);
      setRows(res.data.rows || []);
      setFetched(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (idx) => {
    const p = DATE_PRESETS[idx];
    const from = p.from();
    const to   = p.to();
    setDateFrom(from);
    setDateTo(to);
    setActivePreset(idx);
    fetchLogs({ date_from: from, date_to: to });
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(rows.length / LOG_PAGE_SIZE));
  const pageRows   = rows.slice((page - 1) * LOG_PAGE_SIZE, page * LOG_PAGE_SIZE);

  const streamName = streamId !== "__all__"
    ? (streams.find(s => s.stream_id === streamId)?.name || streamId)
    : "All Streams";

  return (
    <Layout>
      <div data-testid="adspect-logs-page">
        <PageHeader
          eyebrow="Traffic Filter"
          title="Click Logs"
          description="Raw click-by-click data from Adspect — filter by stream and date range."
          actions={
            <Button
              variant="outline"
              size="sm"
              data-testid="refresh-logs-btn"
              onClick={() => fetchLogs()}
              disabled={loading}
            >
              <ArrowsClockwise size={15} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          }
        />

        {/* ── Filter Bar ────────────────────────────────────────────────────── */}
        <div className="rounded-xl border border-border bg-card p-4 mb-6" data-testid="log-filter-bar">
          {/* Row 1: presets + stream selector */}
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <span className="text-xs text-muted-foreground font-medium">Quick:</span>
            {DATE_PRESETS.map((p, idx) => (
              <button
                key={p.label}
                data-testid={`log-preset-${p.label.toLowerCase().replace(" ", "-")}`}
                onClick={() => applyPreset(idx)}
                className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                  activePreset === idx
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                }`}
              >
                {p.label}
              </button>
            ))}

            <div className="flex-1" />

            <Select value={streamId} onValueChange={setStreamId}>
              <SelectTrigger className="w-64 h-8 text-xs" data-testid="log-stream-select">
                <SelectValue placeholder="All Streams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Streams</SelectItem>
                {streams.map((s) => (
                  <SelectItem key={s.stream_id} value={s.stream_id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: date range + load button */}
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-muted-foreground font-medium">From:</span>
            <input
              type="date"
              data-testid="log-date-from"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setActivePreset(-1); }}
              className="text-xs h-8 px-2 rounded-lg border border-border bg-background text-foreground"
            />
            <span className="text-xs text-muted-foreground">To:</span>
            <input
              type="date"
              data-testid="log-date-to"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setActivePreset(-1); }}
              className="text-xs h-8 px-2 rounded-lg border border-border bg-background text-foreground"
            />
            <Button
              size="sm"
              data-testid="load-logs-btn"
              onClick={() => fetchLogs()}
              disabled={loading}
              className="h-8"
            >
              <ChartBar size={13} className="mr-1.5" />
              {loading ? "Loading…" : "Load Logs"}
            </Button>

            {fetched && (
              <span className="text-xs text-muted-foreground ml-2" data-testid="log-row-count">
                {rows.length.toLocaleString()} rows · {streamName}
              </span>
            )}
          </div>
        </div>

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        {!fetched ? (
          <div
            className="border border-dashed border-border rounded-xl p-16 text-center text-muted-foreground text-sm"
            data-testid="log-empty-state"
          >
            Select a date range and click "Load Logs" to view click data
          </div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-16 text-center text-muted-foreground text-sm">
            No clicks found for the selected filters
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden" data-testid="log-table">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-muted-foreground text-xs uppercase tracking-wide">
                    <th className="text-left px-3 py-3 whitespace-nowrap">#</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">Timestamp</th>
                    <th className="text-left px-3 py-3 whitespace-nowrap">IP Address</th>
                    <th className="text-left px-3 py-3">Country</th>
                    <th className="text-left px-3 py-3">OS</th>
                    <th className="text-left px-3 py-3">Browser</th>
                    <th className="text-left px-3 py-3">Mode</th>
                    <th className="text-left px-3 py-3">Result</th>
                    <th className="text-left px-3 py-3">Tags</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, i) => (
                    <LogRow
                      key={i}
                      row={row}
                      index={(page - 1) * LOG_PAGE_SIZE + i + 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20"
                data-testid="log-pagination"
              >
                <span className="text-xs text-muted-foreground">
                  Page {page} of {totalPages} &middot; {rows.length.toLocaleString()} total rows
                </span>
                <div className="flex gap-1.5">
                  <Button
                    variant="outline" size="sm" className="h-7 px-3 text-xs"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >← Prev</Button>
                  {/* Page number pills */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, k) => {
                    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                    const p = start + k;
                    return (
                      <Button
                        key={p}
                        variant={page === p ? "default" : "outline"}
                        size="sm"
                        className="h-7 w-7 p-0 text-xs"
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline" size="sm" className="h-7 px-3 text-xs"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >Next →</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

// ─── Row component ────────────────────────────────────────────────────────────

function LogRow({ row, index }) {
  const isMoney  = row.result === "money";
  const modeClr  = MODE_COLORS[row.mode] || "bg-muted/50 text-muted-foreground border-transparent";
  const tags     = Array.isArray(row.tags) ? row.tags : [];

  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
      <td className="px-3 py-2.5 text-muted-foreground tabular-nums">{index}</td>
      <td className="px-3 py-2.5 font-mono whitespace-nowrap text-muted-foreground">{row.timestamp}</td>
      <td className="px-3 py-2.5 font-mono whitespace-nowrap">{row.ip_address}</td>
      <td className="px-3 py-2.5 font-semibold">{row.country_code || "—"}</td>
      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{row.os || "—"}</td>
      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">{row.browser || "—"}</td>
      <td className="px-3 py-2.5">
        <span className={`text-xs px-1.5 py-0.5 rounded-full border ${modeClr}`}>
          {row.mode || "—"}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full border ${
          isMoney
            ? "bg-green-500/10 text-green-400 border-green-500/30"
            : "bg-slate-500/10 text-slate-400 border-slate-500/30"
        }`}>
          {isMoney ? "Money" : "Safe"}
        </span>
      </td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-0.5">
          {tags.slice(0, 5).map((tag) => (
            <span
              key={tag}
              className="text-xs bg-muted/70 px-1 py-0.5 rounded text-muted-foreground"
            >
              {tag}
            </span>
          ))}
          {tags.length > 5 && (
            <span className="text-xs text-muted-foreground">+{tags.length - 5}</span>
          )}
        </div>
      </td>
    </tr>
  );
}
